'use client';

import { useState, useCallback } from 'react';
import { tmaActions } from '../bootstrap/tmaActions';
import { useApiClient } from '../contexts/ApiContext';
import { useRuntime } from '../runtime/RuntimeContext';
import { getBotUsername, getMiniAppName } from '../utils/config';
import { bustCache } from '../components/RushMid/render/battleAssetVersion';

// Replay preview: the RushMid loading clip (shared as a silent mpeg4 gif).
const GIF_PATH = bustCache('/battle/loading.mp4');

/** Share a finished match's replay the same way open-game invites do: a bot deep link
 *  (`t.me/<bot>/<app>?startapp=replay_<ID>`) inside a prepared inline message on TMA, with a
 *  web/clipboard fallback. The link OPENS the replay viewer — any signed-in user can watch. */
export function useReplayShare(matchId: string, messageText: string, buttonText: string) {
  const [isSharing, setIsSharing] = useState(false);
  const apiClient = useApiClient();
  const runtime = useRuntime();

  const share = useCallback(async () => {
    if (!matchId || isSharing) return;

    const link =
      runtime === 'web'
        ? `${window.location.origin}/games/rush-mid/replay/${matchId}`
        : `https://t.me/${getBotUsername()}/${getMiniAppName()}?startapp=replay_${matchId}`;

    if (await tmaActions.isShareMessageAvailable()) {
      setIsSharing(true);
      try {
        const { preparedMessageId } = await apiClient.api.userControllerSavePreparedInlineMessage({
          gifUrl: `${window.location.origin}${GIF_PATH}`,
          caption: messageText,
          title: 'SHOT',
          button: { text: buttonText, url: link },
        });
        await tmaActions.shareMessage(preparedMessageId);
      } catch (error: unknown) {
        const e = error as { message?: string; type?: string };
        if (e?.message?.includes('ERR_CANCELLED') || e?.type === 'ERR_CANCELLED') return;
        throw error;
      } finally {
        setIsSharing(false);
      }
    } else if (window.location.hash.includes('tgWebAppData')) {
      await tmaActions.openTelegramLink(
        `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(messageText)}`,
      );
    } else {
      try {
        if (navigator.share) await navigator.share({ text: messageText, url: link });
        else await navigator.clipboard.writeText(link);
      } catch (error: unknown) {
        const name = (error as { name?: string })?.name;
        if (name === 'AbortError' || name === 'NotAllowedError') return;
        throw error;
      }
    }
  }, [matchId, isSharing, apiClient, runtime, messageText, buttonText]);

  return { share, isSharing };
}
